import React, { useState } from "react";
import AppChooser from "../AppChooser/AppChooser";
import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import axios from "axios";

export const INVITE_ERRORS = {
  error_adding_user_UE1: "That email address already has a Mobstr account.",
  error_sending_code: "The user was added, but the invite email could not be sent. Please contact support.",
  not_authorized: "You don't have permission to invite users.",
  no_organization: "Your account isn't linked to an organisation, so you can't invite users.",
  default: "Something went wrong sending the invite. Please try again.",
};

export default function AddAppModal({ showModal, setShowModal }) {
  const [email, setEmail] = useState("");
  const [isAppAdmin, setIsAppAdmin] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  const [isBillingAdmin, setIsBillingAdmin] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [canCreateTags, setCanCreateTags] = useState(false);
  const [canCreateApps, setCanCreateApps] = useState(false);
  const [error, setError] = useState("");

  // The modal stays mounted between openings, so clear the form explicitly;
  // otherwise the last invite's email and roles are still there next time.
  const resetForm = () => {
    setEmail("");
    setIsAppAdmin(false);
    setIsApprover(false);
    setIsBillingAdmin(false);
    setIsUserAdmin(false);
    setCanCreateTags(false);
    setCanCreateApps(false);
    setError("");
  };

  const close = () => {
    resetForm();
    setShowModal(false);
  };

  const queryClient = useQueryClient();

  // Mutations
  const mutation = useMutation(
    () => {
      let token = localStorage.getItem("tokenmob");
      return axios.post(
        `${process.env.REACT_APP_API_ROOT}/adduser`,
        {
          email: email.trim(),
          isApprover: isApprover,
          isAppAdmin: isAppAdmin,
          isBillingAdmin: isBillingAdmin,
          isUserAdmin: isUserAdmin,
          canCreateTags: canCreateTags,
          canCreateApps: canCreateApps,
        },
        {
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + token,
          },
        }
      );
    },
    {
      onSuccess: (res) => {
        // /adduser answers failures with 200 and { error: { code } }.
        const code = res?.data?.error?.code;
        if (code) {
          setError(INVITE_ERRORS[code] || INVITE_ERRORS.default);
          return;
        }
        queryClient.invalidateQueries(["myusers"]);
        close();
      },
      onError: (err) => {
        const code = err?.response?.data?.error?.code || err?.response?.data;
        setError(INVITE_ERRORS[code] || INVITE_ERRORS.default);
      },
    }
  );

  const clickAddApp = (e) => {
    e?.preventDefault();
    if (mutation.isLoading) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Enter a valid email address.");
      return;
    }
    setError("");
    mutation.mutate();
  };

  return (
    <>
      {showModal ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-auto my-6 mx-auto max-w-3xl">
              {/*content*/}
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                {/*header*/}
                <div className="flex items-start justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                  <h3 className="text-3xl font-semibold">Invite a user</h3>
                  <button
                    className="p-1 ml-auto bg-transparent border-0 text-black opacity-5 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                    onClick={close}
                    aria-label="Close dialog"
                  >
                    <span className="bg-transparent text-black opacity-5 h-6 w-6 text-2xl block outline-none focus:outline-none">
                      ×
                    </span>
                  </button>
                </div>
                {/*body*/}
                <div className="relative p-6 flex-auto">
                  <p className="mb-4 text-slate-500 text-sm leading-relaxed">
                    You can add a user to your account enabling them to add apps
                    and view results. Added users will get an email invite to
                    set a password.
                  </p>

                  <form onSubmit={clickAddApp} noValidate>
                    <input
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError("");
                      }}
                      aria-label="Email address"
                      type="email"
                      id="email"
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-mobstr-purple focus:border-mobstr-purple block w-full p-2.5"
                      placeholder="Enter Email Address"
                      required
                    />
                    <div className="grid grid-cols-3 gap-4 mt-6">
                      <div className="flex items-center">
                        <input
                          checked={isApprover}
                          onChange={(e) => setIsApprover(!isApprover)}
                          type="checkbox"
                          id="approver"
                          className=" bg-gray-50 border border-gray-300 text-mobstr-purple text-sm rounded-lg focus:ring-mobstr-purple focus:border-mobstr-purple block p-2.5"
                        />
                        <label
                          htmlFor="approver"
                          className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-500"
                        >
                          Approver
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          checked={isAppAdmin}
                          onChange={(e) => setIsAppAdmin(!isAppAdmin)}
                          type="checkbox"
                          id="appAdmin"
                          className=" bg-gray-50 border border-gray-300 text-mobstr-purple text-sm rounded-lg focus:ring-mobstr-purple focus:border-mobstr-purple block p-2.5"
                        />
                        <label
                          htmlFor="appAdmin"
                          className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-500"
                        >
                          App Admin
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          checked={isBillingAdmin}
                          onChange={(e) => setIsBillingAdmin(!isBillingAdmin)}
                          type="checkbox"
                          id="billingAdmin"
                          className=" bg-gray-50 border border-gray-300 text-mobstr-purple text-sm rounded-lg focus:ring-mobstr-purple focus:border-mobstr-purple block p-2.5"
                        />
                        <label
                          htmlFor="billingAdmin"
                          className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-500"
                        >
                          Billing Admin
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          checked={isUserAdmin}
                          onChange={(e) => setIsUserAdmin(!isUserAdmin)}
                          type="checkbox"
                          id="userAdmin"
                          className=" bg-gray-50 border border-gray-300 text-mobstr-purple text-sm rounded-lg focus:ring-mobstr-purple focus:border-mobstr-purple block p-2.5"
                        />
                        <label
                          htmlFor="userAdmin"
                          className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-500"
                        >
                          User Admin
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          checked={canCreateTags}
                          onChange={(e) => setCanCreateTags(!canCreateTags)}
                          type="checkbox"
                          id="canCreateTags"
                          className=" bg-gray-50 border border-gray-300 text-mobstr-purple text-sm rounded-lg focus:ring-mobstr-purple focus:border-mobstr-purple block p-2.5"
                        />
                        <label
                          htmlFor="canCreateTags"
                          className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-500"
                        >
                          Can Create Tags
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          checked={canCreateApps}
                          onChange={(e) => setCanCreateApps(!canCreateApps)}
                          type="checkbox"
                          id="canCreateApps"
                          className=" bg-gray-50 border border-gray-300 text-mobstr-purple text-sm rounded-lg focus:ring-mobstr-purple focus:border-mobstr-purple block p-2.5"
                        />
                        <label
                          htmlFor="canCreateApps"
                          className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-500"
                        >
                          Can Create Apps
                        </label>
                      </div>
                    </div>
                    {error && (
                      <p role="alert" className="mt-4 text-sm text-red-600">
                        {error}
                      </p>
                    )}
                    {/* lets Enter in the email box submit the invite */}
                    <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
                  </form>
                </div>
                {/*footer*/}
                <div className="flex items-center justify-end p-6 border-t border-solid border-slate-200 rounded-b">
                  <button
                    className="text-red-500 uppercase background-transparent font-bold  px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    type="button"
                    onClick={close}
                  >
                    Close
                  </button>
                  <button
                    className="text-white bg-[#2c4251] hover:bg-[#3d627a] disabled:opacity-60 font-medium rounded text-sm w-full sm:w-auto px-5 py-2.5 text-center uppercase"
                    type="button"
                    onClick={clickAddApp}
                    disabled={mutation.isLoading}
                  >
                    {mutation.isLoading ? "Sending…" : "Invite"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}
    </>
  );
}
